import { ChangeDetectionStrategy, Component, Input, input, OnInit } from '@angular/core';
import { ComponentStatus, StateComponent } from '../../../../../../../components/state/state.component';
import { IFormBettings, IFormConfig, IMatchBetting, IMatchFormConfig, ITeamLookupDto, ITeamsMetadata, MatchDuration, MatchWinner, TeamOrNone, UnderOver } from '@oogaday/shared';
import { UntypedFormGroup } from '@angular/forms';
import { IBettingOption, IDropdownConfig } from '../common/model/betting.model';
import { UtilsService } from '../../../../../../../services/utils.service';
import { OptionsProviderService } from '../common/services/options-provider.service';
import { DropdownsBettingComponent } from '../common/components/dropdowns-betting/dropdowns-betting.component';
import { ButtonGroupBettingComponent } from '../common/components/button-group-betting/button-group-betting.component';
import { MatCard, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle } from '@angular/material/card';


@Component({
  selector: 'oo-match-form-filler',
  templateUrl: './match-form-filler.component.html',
  styleUrls: ['./match-form-filler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCard, MatCardHeader, MatCardTitle, MatCardContent, ButtonGroupBettingComponent, DropdownsBettingComponent, MatCardSubtitle],
})
export class MatchFormFillerComponent extends StateComponent<MatchFormFillerComponentState> implements OnInit {


  readonly metadata = input.required<ITeamsMetadata>();

  state: MatchFormFillerComponentState = {
    status: 'ready',
    config: null!,
    betting: undefined,
    bettingForm: null!,
    homeTeam: null!,
    awayTeam: null!,
    exactScoreOptions: [],
    underOverOptions: [],
    winnerOptions: [],
    numberOfCornersOptions: [],
    goalsDiffOptions: [],
    matchDurationOptions: [],
    firstTeamToScoreOptions: [],
    firstTeamToGetCardOptions: [],
    numberOfCardsOptions: [],
    firstTeamToHaveCornerOptions: [],
  };


  @Input() set config(config: IFormConfig) {
    this.updateState({ config: config as IMatchFormConfig });
  }

  @Input() set bettingForm(bettingForm: UntypedFormGroup) {
    this.updateState({ bettingForm });
  }

  @Input() set betting(betting: IFormBettings<any> | undefined) {
    this.updateState({ betting });
  }

  ngOnInit(): void {
    this.updateState({
      homeTeam: this.metadata().teams.find(({ id }) => this.state.config.homeTeamId === id),
      awayTeam: this.metadata().teams.find(({ id }) => this.state.config.awayTeamId === id),
    });

    this.updateState({
      winnerOptions: this.getMatchWinnerOptions(),
      underOverOptions: OptionsProviderService.getUnderOverOptions(3),
      exactScoreOptions: this.getExactScoreOptions(),
      numberOfCornersOptions: OptionsProviderService.getRangeOptions(40),
      goalsDiffOptions: OptionsProviderService.getGoalDiffOptions(this.state.homeTeam.name, this.state.awayTeam.name, false),
      matchDurationOptions: OptionsProviderService.getMatchDurationOptions(),
      firstTeamToScoreOptions: this.firstTeamTo(),
      firstTeamToGetCardOptions: this.firstTeamTo(),
      numberOfCardsOptions: OptionsProviderService.getRangeOptions(30),
      firstTeamToHaveCornerOptions: this.firstTeamTo(),
    });
  }

  private getMatchWinnerOptions(): IBettingOption<MatchWinner>[] {
    return [
      {
        value: this.state.homeTeam.id,
        name: this.state.homeTeam.name,
        subtitle: `(${this.state.config.betsConfig.winner.odds[0]})`,
      },
      {
        value: 'tie',
        name: 'Tie',
        subtitle: `(${this.state.config.betsConfig.winner.odds[1]})`,
      },
      {
        value: this.state.awayTeam.id,
        name: this.state.awayTeam.name,
        subtitle: `(${this.state.config.betsConfig.winner.odds[2]})`,
      },
    ];
  }

  private firstTeamTo(): IBettingOption<TeamOrNone>[] {
    return [
      {
        value: this.state.homeTeam.id,
        name: this.state.homeTeam.name,
      },
      {
        value: 'none',
        name: 'None',
      },
      {
        value: this.state.awayTeam.id,
        name: this.state.awayTeam.name,
      },
    ];
  }

  private getExactScoreOptions(): IDropdownConfig[] {
    const range = UtilsService.genRangeOptions(21);

    return [
      {
        id: this.state.homeTeam.id,
        label: this.state.homeTeam.name,
        hint: 'Home',
        options: range,
      },
      {
        id: this.state.awayTeam.id,
        label: this.state.awayTeam.name,
        hint: 'Away',
        options: range,
      },
    ];
  }
}

interface MatchFormFillerComponentState {
  status: ComponentStatus;
  config: IMatchFormConfig;
  betting: IMatchBetting | undefined;
  homeTeam: ITeamLookupDto;
  awayTeam: ITeamLookupDto;
  bettingForm: UntypedFormGroup;
  winnerOptions: IBettingOption<MatchWinner>[];
  underOverOptions: IBettingOption<UnderOver>[];
  exactScoreOptions: IDropdownConfig[];
  numberOfCornersOptions: IDropdownConfig[];
  goalsDiffOptions: IDropdownConfig[];
  matchDurationOptions: IDropdownConfig<MatchDuration>[];
  firstTeamToScoreOptions: IBettingOption<TeamOrNone>[];
  firstTeamToGetCardOptions: IBettingOption<TeamOrNone>[];
  numberOfCardsOptions: IDropdownConfig[];
  firstTeamToHaveCornerOptions: IBettingOption<TeamOrNone>[];
}
